import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Save, Loader2, ArrowLeft, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, api } from '../App';
import { ASSETS } from '../constants/site';

const ROLE_STYLES = {
  admin: 'text-amber-300 border-amber-500/40',
  developer: 'text-sky-300 border-sky-500/40',
  sales: 'text-emerald-300 border-emerald-500/40',
  customer: 'text-slate-400 border-slate-600/50',
};

function MemberRow({ member, onSaved }) {
  const [role, setRole] = useState(member.role);
  const [target, setTarget] = useState(member.target_sales || 0);
  const [code, setCode] = useState(member.sales_code || '');
  const [commission, setCommission] = useState(member.commission_pct || 0);
  const [busy, setBusy] = useState(false);
  const dirty = role !== member.role || Number(target) !== (member.target_sales || 0)
    || code !== (member.sales_code || '') || Number(commission) !== (member.commission_pct || 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/team/${member.user_id}`, {
        role,
        target_sales: Number(target) || 0,
        sales_code: code,
        commission_pct: Number(commission) || 0,
      });
      toast.success(`${member.email} updated.`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update member.');
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async () => {
    try {
      await api.post(`/team/${member.user_id}/payout`);
      toast.success(`Commission marked paid for ${member.email}.`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mark payout.');
    }
  };

  const inputCls = 'bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-lg px-3 py-2 text-xs outline-none transition-colors';

  return (
    <div data-testid="team-member-row" className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-5">
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3 min-w-[220px]">
          {member.picture ? (
            <img src={member.picture} alt="" className="h-9 w-9 rounded-full border border-slate-700" />
          ) : (
            <span className="h-9 w-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 text-xs font-bold">
              {member.name?.[0] || member.email[0].toUpperCase()}
            </span>
          )}
          <div>
            <div className="text-sm text-slate-200">{member.name || '—'}</div>
            <div className="text-xs text-slate-500">{member.email}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="team-role-select" value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
            {['customer', 'sales', 'developer', 'admin'].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {role === 'sales' && (
            <>
              <input data-testid="team-sales-code-input" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="Code e.g. GS-SAM" className={`${inputCls} w-28 font-mono-tech uppercase`} />
              <input data-testid="team-target-input" type="number" min="0" value={target} onChange={(e) => setTarget(e.target.value)}
                placeholder="Target" className={`${inputCls} w-20`} title="Monthly sales target" />
              <input data-testid="team-commission-input" type="number" min="0" max="100" step="0.5" value={commission}
                onChange={(e) => setCommission(e.target.value)} placeholder="Comm %" className={`${inputCls} w-24`} title="Commission percent" />
            </>
          )}
          <button data-testid="team-save-button" onClick={save} disabled={!dirty || busy}
            className="inline-flex items-center gap-1.5 text-xs bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-4 py-2 disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-6 mt-4 pt-4 border-t border-slate-800/60">
        <span className={`font-mono-tech text-[10px] tracking-widest border rounded-full px-3 py-1 ${ROLE_STYLES[member.role]}`}>
          {member.role.toUpperCase()}
        </span>
        {member.sales_code && (
          <>
            <span className="font-mono-tech text-[11px] text-slate-400">CODE {member.sales_code}</span>
            <span className="inline-flex items-center gap-1.5 font-mono-tech text-[11px] text-emerald-300">
              <TrendingUp size={12} /> {member.sales_count} SALES · ${Number(member.sales_total).toLocaleString()}
            </span>
            {member.target_sales > 0 && (
              <span className="font-mono-tech text-[11px] text-slate-500">TARGET {member.target_sales}/MO</span>
            )}
            {member.commission_pct > 0 && (
              <span data-testid="commission-owed" className="font-mono-tech text-[11px] text-amber-300">
                COMMISSION {member.commission_pct}% · OWES ${Number(member.commission_owed).toLocaleString()} THIS MONTH
              </span>
            )}
            {member.commission_owed > 0 && !member.commission_paid && (
              <button data-testid="payout-mark-paid-button" onClick={markPaid}
                className="text-[11px] border border-emerald-500/40 text-emerald-300 rounded-full px-3 py-1 hover:bg-emerald-500/10 transition-colors">
                Mark paid
              </button>
            )}
            {member.commission_paid && (
              <span className="font-mono-tech text-[11px] text-emerald-400/70">✓ PAID THIS MONTH</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [team, setTeam] = useState(null);

  const load = () => api.get('/team').then((r) => setTeam(r.data)).catch(() => setTeam([]));
  useEffect(() => {
    if (user?.role === 'admin') load();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
        <div className="font-mono-tech text-amber-400/80 text-sm tracking-widest animate-pulse">VERIFYING SESSION…</div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6">
        <div className="gold-ring rounded-3xl bg-[#0F1C33]/70 p-10 max-w-md w-full text-center">
          <img src={ASSETS.icon} alt="GSLAB" className="h-16 w-16 rounded-xl mx-auto mb-6" />
          <h1 className="font-display font-bold text-2xl mb-3">Admin only</h1>
          <p className="text-sm text-slate-400 mb-8">
            {user ? `Signed in as ${user.email} (${user.role}) — this page is for GSLAB admins.` : 'Sign in with an admin Google account.'}
          </p>
          {!user && (
            <>
              <button data-testid="admin-login-button" onClick={login}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-6 py-3.5">
                Sign in with Google
              </button>
              <a href="/login" className="block text-xs text-slate-400 hover:text-amber-300 mt-4 transition-colors">
                or sign in with email &amp; password
              </a>
            </>
          )}
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mt-6 transition-colors">
            <ArrowLeft size={13} /> Back to gslab.engineer
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050B14] pt-28 pb-20 px-4 sm:px-8">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3">
              <Users size={24} className="text-amber-400" /> Team & Roles
            </h1>
            <p className="text-sm text-slate-500 mt-2">Elevate roles, set sales codes & targets, track commissions.</p>
          </div>
          <Link to="/account" className="text-sm text-slate-400 hover:text-amber-300 transition-colors">← Portal</Link>
        </motion.div>

        {team === null ? (
          <div className="flex justify-center py-16"><Loader2 size={22} className="text-amber-400 animate-spin" /></div>
        ) : team.length === 0 ? (
          <p className="text-sm text-slate-500 border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-8 text-center">
            No users yet — teammates appear here after their first Google sign-in.
          </p>
        ) : (
          <div className="space-y-4">
            {team.map((m) => <MemberRow key={m.user_id} member={m} onSaved={load} />)}
          </div>
        )}

        <div className="mt-10 border border-slate-800 rounded-2xl bg-[#0F1C33]/40 p-6 text-xs text-slate-500 leading-relaxed">
          <p className="font-mono-tech tracking-widest text-slate-400 mb-2">ROLE GUIDE</p>
          <p><span className="text-amber-300">ADMIN</span> — everything: this page, projects, bookings, calendar blocks, review link.</p>
          <p><span className="text-sky-300">DEVELOPER</span> — build control: client projects, milestones, bookings, calendar blocks.</p>
          <p><span className="text-emerald-300">SALES</span> — gets a referral code; every paid checkout through their link (gslab.engineer/?ref=CODE) counts toward their target.</p>
          <p><span className="text-slate-400">CUSTOMER</span> — default; sees their own plans and projects.</p>
        </div>
      </div>
    </div>
  );
}
